import { useCallback, useEffect, useMemo, useState } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  createFinanceCategory,
  deleteFinanceCategory,
  getFinanceAccounts,
  getFinanceCategories,
  getFinanceRules,
  getFinanceStatus,
  getFinanceSummary,
  getFinanceTransactions,
  setFinanceTransactionCategory,
  setFinanceTransactionSplits,
} from "../utils/apiClient";
import FinanceRulesPanel from "../components/finance/FinanceRulesPanel";
import AccountSelect from "../components/finance/AccountSelect";
import FinanceTransactionList from "../components/finance/FinanceTransactionList";
import FinanceBudgetPanel from "../components/finance/FinanceBudgetPanel";
import FinanceBurnupChart from "../components/finance/FinanceBurnupChart";
import FinanceBudgetRemainingChart from "../components/finance/FinanceBudgetRemainingChart";
import FinanceCategoryBreakdown from "../components/finance/FinanceCategoryBreakdown";
import FinanceCategoryFilter from "../components/finance/FinanceCategoryFilter";
import {
  MONTH_LAST_BANK_DAY,
  addDays,
  isoDate,
  monthBounds,
  monthKey,
} from "../utils/financeCalendar";
import {
  categoryIdsInPeriod,
  totalsFromTransactions,
  transactionMatchesCategoryFilter,
} from "../utils/financeCategoryFilter";
import { FINANCE_SYNCED_EVENT } from "../utils/financeSync";

const TOTAL_ACCOUNT = "total";

function parseIso(value) {
  const [year, month, day] = String(value || "").split("-").map(Number);
  if (!year || !month || !day) return new Date();
  return new Date(year, month - 1, day);
}

function weekRange(d) {
  const weekday = d.getDay() === 0 ? 6 : d.getDay() - 1;
  const start = addDays(d, -weekday);
  return { from: isoDate(start), to: isoDate(addDays(start, 6)) };
}

function rangeForVisPer(visPer, anchor, monthMode) {
  return visPer === "week" ? weekRange(anchor) : monthBounds(anchor, monthMode);
}

function formatMoney(amount, currency = "DKK") {
  return Number(amount || 0).toLocaleString("da-DK", {
    style: "currency",
    currency,
  });
}

function pickDefaultAccount(accounts) {
  if (!accounts.length) return "";
  return (
    accounts.find((account) => account.is_default)?.account_uuid ||
    accounts.find((account) => account.monitored)?.account_uuid ||
    accounts[0].account_uuid
  );
}

function weekStartIso(iso) {
  const weekday = parseIso(iso).getDay() === 0 ? 6 : parseIso(iso).getDay() - 1;
  return isoDate(addDays(parseIso(iso), -weekday));
}

function formatGroupLabel(key, visPer) {
  if (visPer === "month") {
    const [year, month] = key.split("-").map(Number);
    return new Date(year, month - 1, 1).toLocaleDateString("da-DK", {
      month: "long",
      year: "numeric",
    });
  }
  const start = parseIso(key);
  const end = addDays(start, 6);
  return `Uge · ${start.toLocaleDateString("da-DK")} – ${end.toLocaleDateString("da-DK")}`;
}

function groupTransactions(transactions, visPer, monthMode) {
  const order = [];
  const map = new Map();
  (transactions || []).forEach((tx) => {
    const key = visPer === "week" ? weekStartIso(tx.booking_date) : monthKey(tx.booking_date, monthMode);
    if (!map.has(key)) {
      map.set(key, []);
      order.push(key);
    }
    map.get(key).push(tx);
  });
  return order.map((key) => ({
    key,
    label: formatGroupLabel(key, visPer),
    rows: map.get(key),
  }));
}

function Finance() {
  const [tab, setTab] = useState("overview");
  const [monthMode, setMonthMode] = useState(MONTH_LAST_BANK_DAY);
  const initialMonth = monthBounds(new Date(), MONTH_LAST_BANK_DAY);
  const [period, setPeriod] = useState("month");
  const [dateFrom, setDateFrom] = useState(initialMonth.from);
  const [dateTo, setDateTo] = useState(initialMonth.to);
  const [accountUuid, setAccountUuid] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [status, setStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryParent, setNewCategoryParent] = useState("");
  const household = accountUuid === TOTAL_ACCOUNT;

  const visibleTransactions = useMemo(
    () => (transactions || []).filter((tx) => transactionMatchesCategoryFilter(tx, categoryFilter)),
    [transactions, categoryFilter]
  );

  const transactionGroups = useMemo(
    () => groupTransactions(visibleTransactions, period, monthMode),
    [visibleTransactions, period, monthMode]
  );

  const overviewTotals = useMemo(() => {
    if (!categoryFilter.length && summary) {
      return {
        income: Number(summary.income),
        expenses: Number(summary.expenses),
        net: Number(summary.net),
      };
    }
    return totalsFromTransactions(transactions, categoryFilter, household);
  }, [categoryFilter, household, summary, transactions]);

  useEffect(() => {
    const present = categoryIdsInPeriod(transactions);
    setCategoryFilter((ids) => {
      const next = (ids || []).filter((id) => present.has(String(id)));
      return next.length === ids.length ? ids : next;
    });
  }, [transactions]);

  const loadMeta = useCallback(async () => {
    const [st, acc, cats, rls] = await Promise.all([
      getFinanceStatus(),
      getFinanceAccounts(),
      getFinanceCategories(),
      getFinanceRules(),
    ]);
    setStatus(st);
    if (st?.finance_month_mode) setMonthMode(st.finance_month_mode);
    const list = Array.isArray(acc) ? acc : [];
    setAccounts(list);
    setCategories(Array.isArray(cats) ? cats : []);
    setRules(Array.isArray(rls) ? rls : []);
    return list;
  }, []);

  const loadOverview = useCallback(async (uuid) => {
    const selected = uuid !== undefined ? uuid : accountUuid;
    const isTotal = selected === TOTAL_ACCOUNT;
    if (!isTotal && !selected) {
      setSummary(null);
      setTransactions([]);
      return;
    }
    const params = { period, date_from: dateFrom, date_to: dateTo };
    if (!isTotal) params.account_uuid = selected;
    const sum = await getFinanceSummary(params);
    const txParams = {
      date_from: sum.date_from,
      date_to: sum.date_to,
    };
    if (!isTotal) txParams.account_uuid = selected;
    const txs = await getFinanceTransactions(txParams);
    setSummary(sum);
    setTransactions(Array.isArray(txs) ? txs : []);
  }, [accountUuid, dateFrom, dateTo, period]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const list = await loadMeta();
      const uuid =
        accountUuid === TOTAL_ACCOUNT
          ? TOTAL_ACCOUNT
          : accountUuid && list.some((account) => account.account_uuid === accountUuid)
            ? accountUuid
            : pickDefaultAccount(list);
      if (uuid !== accountUuid) setAccountUuid(uuid);
      await loadOverview(uuid);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Kunne ikke hente økonomi");
    } finally {
      setLoading(false);
    }
  }, [accountUuid, loadMeta, loadOverview]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    const onSynced = () => {
      loadOverview();
    };
    window.addEventListener(FINANCE_SYNCED_EVENT, onSynced);
    return () => window.removeEventListener(FINANCE_SYNCED_EVENT, onSynced);
  }, [loadOverview]);

  useEffect(() => {
    if (period !== "month") return;
    const range = monthBounds(parseIso(dateFrom), monthMode);
    if (range.from !== dateFrom || range.to !== dateTo) {
      setDateFrom(range.from);
      setDateTo(range.to);
    }
  }, [monthMode]);

  const applyVisPer = (next) => {
    setPeriod(next);
    const range = rangeForVisPer(next, parseIso(dateFrom), monthMode);
    setDateFrom(range.from);
    setDateTo(range.to);
  };

  const onDateFromChange = (value) => {
    setDateFrom(value);
    if (value && dateTo && value > dateTo) setDateTo(value);
  };

  const onDateToChange = (value) => {
    setDateTo(value);
    if (value && dateFrom && value < dateFrom) setDateFrom(value);
  };

  const consentWarning = (status?.sessions || []).some((session) => {
    if (!session.valid_until) return false;
    const left = new Date(session.valid_until).getTime() - Date.now();
    return session.status !== "active" || left < 14 * 24 * 60 * 60 * 1000;
  });

  const setTransactionCategory = async (transactionUuid, categoryUuid, exemptFromBudget, splits) => {
    setError("");
    try {
      await setFinanceTransactionCategory(transactionUuid, categoryUuid, exemptFromBudget);
      if (splits !== undefined) {
        await setFinanceTransactionSplits(transactionUuid, splits);
      }
      await loadOverview();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Kunne ikke sætte kategori");
      throw err;
    }
  };

  const addCategory = async (event) => {
    event.preventDefault();
    if (!newCategoryName.trim()) return;
    setError("");
    try {
      await createFinanceCategory({
        name: newCategoryName.trim(),
        parent_uuid: newCategoryParent || null,
      });
      setNewCategoryName("");
      setNewCategoryParent("");
      setCategories(await getFinanceCategories());
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Kunne ikke oprette kategori");
    }
  };

  const removeCategory = async (categoryUuid) => {
    setError("");
    try {
      await deleteFinanceCategory(categoryUuid);
      setCategories(await getFinanceCategories());
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Kunne ikke slette kategori");
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
        Økonomi
      </Typography>

      {status && !status.configured ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Enable Banking er ikke konfigureret. Tilføj nøglerne i secrets.toml.
        </Alert>
      ) : null}
      {consentWarning ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Bankgodkendelsen er udløbet eller udløber inden 14 dage. Forbind kontoen igen under Profil → Bank.
        </Alert>
      ) : null}
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}

      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 3 }} variant="scrollable">
        <Tab value="overview" label="Overblik" />
        <Tab value="budget" label="Budget" />
        <Tab value="categories" label="Kategorier" />
        <Tab value="rules" label="Regler" />
      </Tabs>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : null}

      {!loading && tab === "overview" ? (
        <Box>
          <Stack spacing={2} sx={{ mb: 3 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
              <TextField
                size="small"
                type="date"
                label="Fra"
                value={dateFrom}
                onChange={(e) => onDateFromChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small"
                type="date"
                label="Til"
                value={dateTo}
                onChange={(e) => onDateToChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Vis per</InputLabel>
                <Select
                  label="Vis per"
                  value={period}
                  onChange={(e) => applyVisPer(e.target.value)}
                >
                  <MenuItem value="month">Måned</MenuItem>
                  <MenuItem value="week">Uge</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <AccountSelect
              accounts={accounts}
              value={accountUuid}
              onChange={setAccountUuid}
              showTotal
              sx={{ minWidth: 280, maxWidth: 480 }}
            />
            <FinanceCategoryFilter
              categories={categories}
              transactions={transactions}
              value={categoryFilter}
              onChange={setCategoryFilter}
              sx={{ minWidth: 280, maxWidth: 480 }}
            />
          </Stack>

          {summary ? (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 3 }}>
              {[
                ["Indtægt", overviewTotals.income, null],
                ["Udgift", overviewTotals.expenses, "budget"],
                ["Netto", overviewTotals.net, "budget"],
              ].map(([label, value, budgetHint]) => {
                const budgetDiff =
                  budgetHint && summary.budget_period != null
                    ? Number(summary.budget_period) - Number(overviewTotals.expenses)
                    : null;
                return (
                  <Box key={label} sx={{ flex: 1, p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {label}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {formatMoney(value)}
                    </Typography>
                    {budgetDiff != null ? (
                      <Typography
                        variant="body2"
                        sx={{
                          color: budgetDiff < 0 ? "error.main" : "success.main",
                          fontWeight: 600,
                        }}
                      >
                        ({formatMoney(budgetDiff)})
                      </Typography>
                    ) : null}
                  </Box>
                );
              })}
            </Stack>
          ) : null}

          {summary && summary.budget_period != null ? (
            <Box sx={{ mb: 3, p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="space-between">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Budget for perioden
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {formatMoney(summary.budget_period)}
                  </Typography>
                  {summary.budget_monthly != null ? (
                    <Typography variant="caption" color="text.secondary">
                      {accountUuid === TOTAL_ACCOUNT
                        ? `Sum af konti ${formatMoney(summary.budget_monthly)} / måned`
                        : `Månedsbudget ${formatMoney(summary.budget_monthly)}`}
                    </Typography>
                  ) : null}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Udgift
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {formatMoney(overviewTotals.expenses)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {Number(overviewTotals.expenses) > Number(summary.budget_period) ? "Over budget" : "Tilbage"}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color:
                        Number(overviewTotals.expenses) > Number(summary.budget_period)
                          ? "error.main"
                          : "success.main",
                    }}
                  >
                    {formatMoney(Math.abs(Number(summary.budget_period) - Number(overviewTotals.expenses)))}
                  </Typography>
                </Box>
              </Stack>
              <Box sx={{ height: 8, bgcolor: "action.hover", borderRadius: 1, overflow: "hidden", mt: 1.5 }}>
                <Box
                  sx={{
                    height: "100%",
                    width: `${Math.min(100, (Math.abs(Number(overviewTotals.expenses)) / Math.max(Number(summary.budget_period), 1)) * 100)}%`,
                    bgcolor:
                      Number(overviewTotals.expenses) > Number(summary.budget_period)
                        ? "error.main"
                        : "success.main",
                  }}
                />
              </Box>
            </Box>
          ) : null}

          {summary ? (
            <FinanceCategoryBreakdown
              summary={summary}
              formatMoney={formatMoney}
              selectedIds={categoryFilter}
            />
          ) : null}

          {summary ? (
            <FinanceBurnupChart
              transactions={transactions}
              categories={categories}
              dateFrom={summary.date_from}
              dateTo={summary.date_to}
              household={household}
              formatMoney={formatMoney}
              selectedIds={categoryFilter}
            />
          ) : null}

          {summary ? (
            <FinanceBudgetRemainingChart
              transactions={transactions}
              dateFrom={summary.date_from}
              dateTo={summary.date_to}
              household={household}
              budgetMonthly={summary.budget_monthly}
              monthMode={monthMode}
              formatMoney={formatMoney}
              selectedIds={categoryFilter}
              emptyHint={
                household
                  ? "Sæt et månedsbudget på mindst én konto under Budget."
                  : "Sæt et månedsbudget under fanen Budget for denne konto."
              }
            />
          ) : null}

          <Accordion
            defaultExpanded={false}
            disableGutters
            elevation={0}
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 600 }}>
                Posteringer ({visibleTransactions.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 1, pt: 0 }}>
              <FinanceTransactionList
                groups={transactionGroups}
                formatMoney={formatMoney}
                emptyText="Ingen posteringer i perioden."
                categories={categories}
                onCategoryChange={setTransactionCategory}
              />
            </AccordionDetails>
          </Accordion>
        </Box>
      ) : null}

      {!loading && tab === "budget" ? (
        <FinanceBudgetPanel
          accounts={accounts}
          onAccountsChange={setAccounts}
          onError={setError}
          formatMoney={formatMoney}
        />
      ) : null}

      {!loading && tab === "categories" ? (
        <Box>
          <Box component="form" onSubmit={addCategory} sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 3 }}>
            <TextField
              size="small"
              label="Ny kategori"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Under</InputLabel>
              <Select
                label="Under"
                value={newCategoryParent}
                onChange={(e) => setNewCategoryParent(e.target.value)}
              >
                <MenuItem value="">Topniveau</MenuItem>
                {categories.filter((cat) => !cat.system_key).map((cat) => (
                  <MenuItem key={cat.category_uuid} value={cat.category_uuid}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button type="submit" variant="contained">
              Tilføj
            </Button>
          </Box>
          {categories.map((cat) => (
            <Box key={cat.category_uuid} sx={{ mb: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontWeight: 700 }}>{cat.name}</Typography>
                {cat.system_key ? (
                  <Typography variant="caption" color="text.secondary">
                    System · tæller ikke med i totaler
                  </Typography>
                ) : (
                  <IconButton size="small" aria-label="Slet" onClick={() => removeCategory(cat.category_uuid)}>
                    <DeleteOutlinedIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
              {(cat.children || []).map((child) => (
                <Stack key={child.category_uuid} direction="row" alignItems="center" spacing={1} sx={{ pl: 2 }}>
                  <Typography variant="body2">{child.name}</Typography>
                  <IconButton size="small" aria-label="Slet" onClick={() => removeCategory(child.category_uuid)}>
                    <DeleteOutlinedIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Box>
          ))}
        </Box>
      ) : null}

      {!loading && tab === "rules" ? (
        <FinanceRulesPanel
          accounts={accounts}
          categories={categories}
          rules={rules}
          onRulesChange={(next) => {
            setRules(next);
            loadOverview();
          }}
          onError={setError}
          formatMoney={formatMoney}
        />
      ) : null}
    </Box>
  );
}

export default Finance;
